import { useMemo, useState } from 'react'

import { DashCard } from './DashboardCards'
import { useHodDirectory } from '../../../hooks/useHodDirectory'
import { tallyTop, type TallyResult, type TallyRow } from '../../../lib/dashboard-insights'
import { ageDays } from '../../../lib/issues'
import { resolutionStatus, type ResolutionTargets } from '../../../lib/resolution-targets'
import { SegmentedControl } from '../../ui/SegmentedControl'
import { SEV_COLOR, SEV_LETTER } from '../issues/IssuesListRow'

import type { HodDirectoryEntry } from '../../../api/hodApi'
import type { GisIssue } from '../../../api/types'
import type { BoardShortcuts } from '../../../hooks/useBoardShortcuts'

/** The team's "separate by" cuts: per-issue, per-HOD, per-department, per-issue-type (+ question). */
type BreakdownView = 'issues' | 'hod' | 'department' | 'category' | 'question'

const VIEW_ITEMS: { key: BreakdownView; label: string }[] = [
  { key: 'issues', label: 'Issues' },
  { key: 'hod', label: 'HOD' },
  { key: 'department', label: 'Department' },
  { key: 'category', label: 'Issue type' },
  { key: 'question', label: 'Question' },
]

const CAPTION: Record<BreakdownView, string> = {
  issues: 'Highest risk first',
  hod: 'Open issues per HOD',
  department: 'Open issues per department',
  category: 'Open issues per issue type',
  question: 'Open issues per survey question',
}

const TOP_ISSUES = 7
const TOP_TALLY = 6

/** Photo thumb, or the severity gradient the gallery cards use when the issue has no photo. */
function Thumb({ issue }: { issue: GisIssue }) {
  return (
    <span className="relative block h-[38px] w-[52px] flex-none overflow-hidden rounded-[8px] bg-gis-track-2">
      {issue.photoUrl ? (
        <img src={issue.photoUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <span
          className="block h-full w-full"
          style={{ background: `linear-gradient(150deg, ${SEV_COLOR[issue.severity]}, #16243f)` }}
          aria-hidden
        />
      )}
      <span
        className="absolute top-[3px] left-[3px] grid h-[14px] w-[14px] place-items-center rounded-[5px] text-[9px] font-bold text-white"
        style={{ background: SEV_COLOR[issue.severity] }}
      >
        {SEV_LETTER[issue.severity]}
      </span>
    </span>
  )
}

/** The per-issue rows (photo + title + age + past-target flag + site · department). */
function IssueRows({ rows, targets, onOpen }: {
  rows: GisIssue[]
  targets: ResolutionTargets
  onOpen: (id: string, siteId: string | null) => void
}) {
  return (
    <div className="flex flex-col gap-[5px]">
      {rows.map((i) => {
        const age = ageDays(i.createdAt)
        const breach = resolutionStatus(i.severity, age, targets)
        const where = [i.siteName, i.department].filter(Boolean).join(' · ')
        return (
          <button
            key={i.id}
            type="button"
            onClick={() => onOpen(i.id, i.siteId)}
            title="Open this issue's full details"
            className="-mx-1 flex cursor-pointer items-center gap-[10px] rounded-[9px] px-1 py-[3px] text-left transition-colors hover:bg-[#f5f7fa] focus-visible:ring-2 focus-visible:ring-blue-500/60"
          >
            <Thumb issue={i} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12px] font-semibold text-gis-ink">{i.title}</span>
              <span className="mt-[1px] flex items-center gap-[6px] text-[10.5px] text-gis-soft">
                <span className="font-bold tabular-nums text-gis-ink-2">{age}d</span>
                {breach !== 'on-track' && (
                  <span className={`font-bold ${breach === 'overdue' ? 'text-gis-red' : 'text-gis-orange'}`}>
                    {breach === 'overdue' ? 'past target' : 'due soon'}
                  </span>
                )}
                {where !== '' && <span className="min-w-0 truncate">{where}</span>}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** The tally rows shared by the HOD/Department/Type/Question cuts: label (+sub) · bar (red leading
 *  segment = critical share) · count · critical note. */
function TallyRows({ rows, sub, rowTitle, onPick }: {
  rows: TallyRow[]
  /** Second line under the label (e.g. the HOD's department, a department's HOD); null = none. */
  sub: (r: TallyRow) => string | null
  rowTitle: string
  onPick: (r: TallyRow) => void
}) {
  const max = Math.max(1, ...rows.map((r) => r.count))
  return (
    <div className="flex flex-col gap-[7px]">
      {rows.map((r) => {
        const subText = sub(r)
        return (
          <button
            key={r.label}
            type="button"
            onClick={() => onPick(r)}
            title={rowTitle}
            className="-mx-1 grid cursor-pointer grid-cols-[minmax(0,170px)_minmax(0,1fr)_auto] items-center gap-3 rounded-[8px] px-1 py-[3px] text-left transition-colors hover:bg-[#f5f7fa] focus-visible:ring-2 focus-visible:ring-blue-500/60"
          >
            <span className="min-w-0">
              <span className="block truncate text-[12px] font-semibold text-gis-ink-2" title={r.label}>{r.label}</span>
              {subText !== null && <span className="block truncate text-[10px] text-gis-soft">{subText}</span>}
            </span>
            <span className="flex h-[10px] min-w-0 overflow-hidden rounded-full bg-gis-track-2">
              <span
                className="block h-full bg-gis-red transition-[width] duration-[450ms] ease-[cubic-bezier(.2,.7,.3,1)]"
                style={{ width: `${(r.critical / max) * 100}%` }}
              />
              <span
                className="block h-full bg-gis-navy/55 transition-[width] duration-[450ms] ease-[cubic-bezier(.2,.7,.3,1)]"
                style={{ width: `${((r.count - r.critical) / max) * 100}%` }}
              />
            </span>
            <span className="flex items-center gap-2 text-[10.5px] tabular-nums">
              <span className="min-w-[22px] text-right font-bold text-gis-ink">{r.count}</span>
              <span className={r.critical > 0 ? 'font-semibold text-gis-red' : 'text-gis-soft'}>
                {r.critical > 0 ? `${r.critical} critical` : 'no critical'}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** HOD display name for a department (HOD ↔ department is 1:1), truthful fallback pre-directory. */
const hodLabel = (department: string, directory: HodDirectoryEntry[] | undefined): string =>
  directory?.find((d) => d.department === department)?.hodName ?? `HOD — ${department}`

interface IssueBreakdownCardProps {
  /** Open issues in the current scope. */
  openIssues: GisIssue[]
  /** The same list in the board's risk order (the Issues cut). */
  rankedOpen: GisIssue[]
  targets: ResolutionTargets
  pending: boolean
  onOpenIssue: (id: string, siteId: string | null) => void
  go: BoardShortcuts
}

/**
 * "Issue breakdown" — the open load SEPARATED BY the team's four cuts (plus Question): the riskiest
 * individual issues, or the per-HOD / per-department / per-issue-type / per-question tallies, all in
 * one switchable card. Every row drills to the answer: issues open their full detail; HOD /
 * department / type rows open the Issues board filtered to that value; question rows open it
 * grouped by question.
 */
export function IssueBreakdownCard({ openIssues, rankedOpen, targets, pending, onOpenIssue, go }: IssueBreakdownCardProps) {
  const [view, setView] = useState<BreakdownView>('issues')
  const { data: directory } = useHodDirectory()

  const tally: TallyResult = useMemo(() => {
    if (view === 'category') {
      return tallyTop(openIssues, (i) => i.category, TOP_TALLY, 'Uncategorised')
    }
    if (view === 'question') {
      return tallyTop(openIssues, (i) => i.question, TOP_TALLY, 'No linked question')
    }
    // hod + department both tally the owning department; only label/sub/drill differ.
    return tallyTop(openIssues, (i) => i.department, TOP_TALLY, 'Unmapped')
  }, [openIssues, view])
  // The HOD cut re-labels the department tally with the HOD's name (department becomes the sub-line).
  const rows: TallyRow[] =
    view === 'hod'
      ? tally.rows.map((r) => ({ ...r, label: r.value !== null ? hodLabel(r.value, directory) : r.label }))
      : tally.rows

  const subFor = (r: TallyRow): string | null => {
    if (view === 'hod') {
      return r.value
    }
    if (view === 'department' && r.value !== null) {
      return hodLabel(r.value, directory)
    }
    return null
  }
  const pickFor = (r: TallyRow): void => {
    if (view === 'question') {
      go.goIssuesByQuestion()
      return
    }
    if (r.value === null) {
      go.goIssues() // fallback buckets have no real facet value — plain board, never a lying filter
      return
    }
    if (view === 'hod') {
      go.goIssuesHod(r.value)
    } else if (view === 'department') {
      go.goIssuesDept(r.value)
    } else {
      go.goIssuesCategory(r.value)
    }
  }
  const rowTitle =
    view === 'question'
      ? 'Open the Issues board grouped by question'
      : 'Open the Issues board filtered to this value'

  const issueRows = rankedOpen.slice(0, TOP_ISSUES)
  const moreNote =
    view === 'issues'
      ? rankedOpen.length > TOP_ISSUES
        ? `+${rankedOpen.length - TOP_ISSUES} more open issues — open the Issues board for all of them.`
        : null
      : tally.more > 0
        ? `+${tally.more} more — open the Issues board for the full split.`
        : null

  return (
    <DashCard title="Issue breakdown" onOpen={() => go.goIssues()} openLabel="Open Issues board">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[10.5px] text-gis-soft">{CAPTION[view]}</span>
        <SegmentedControl<BreakdownView>
          aria-label="Separate open issues by"
          size="sm"
          track="a"
          shape="rounded"
          items={VIEW_ITEMS}
          value={view}
          onChange={setView}
        />
      </div>
      {pending ? (
        <div className="flex flex-col gap-2 py-1" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[38px] animate-pulse rounded-[8px] bg-gis-track-2" />
          ))}
        </div>
      ) : openIssues.length === 0 ? (
        <div className="py-4 text-center text-[11.5px] text-gis-soft">No open issues in this selection.</div>
      ) : (
        <>
          {view === 'issues' ? (
            <IssueRows rows={issueRows} targets={targets} onOpen={onOpenIssue} />
          ) : (
            <TallyRows rows={rows} sub={subFor} rowTitle={rowTitle} onPick={pickFor} />
          )}
          {moreNote !== null && <p className="mt-2 text-[9.5px] text-gis-muted">{moreNote}</p>}
        </>
      )}
    </DashCard>
  )
}
